import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppThunk } from '@share/utils';
import { getHeaders, axiosInstance } from '@share/utils';
import { Urls } from '@share/constants';
import { NULL_VALUE } from '@constants';
import { ILuxuryHome, ILuxuryHomeLocation } from '@share/common-types';
import { luxuryHomesSearchActions } from './luxury-homes-search';

export interface ILuxuryHomesState {
  locations: ILuxuryHomeLocation[];
  homes: ILuxuryHome[];
  homesOriginal: ILuxuryHome[];
  selectedHome: ILuxuryHome | null;
  error: string;
  isLoading: boolean;
}

export const luxuryHomesInitialState: ILuxuryHomesState = {
  locations: [],
  homes: [],
  homesOriginal: [],
  selectedHome: NULL_VALUE,
  error: '',
  isLoading: false,
};

const luxuryHomesSlice = createSlice({
  name: 'luxury-homes',
  initialState: luxuryHomesInitialState,
  reducers: {
    setLocations: (state: ILuxuryHomesState, { payload }: PayloadAction<ILuxuryHomeLocation[]>) => {
      state.locations = payload;
    },
    setHomes: (state: ILuxuryHomesState, { payload }: PayloadAction<ILuxuryHome[]>) => {
      state.homes = payload;
    },
    setHomesOriginal: (state: ILuxuryHomesState, { payload }: PayloadAction<ILuxuryHome[]>) => {
      state.homesOriginal = payload;
    },
    setSelectedHome: (state: ILuxuryHomesState, { payload }: PayloadAction<ILuxuryHome>) => {
      state.selectedHome = payload;
    },
    setError: (state: ILuxuryHomesState, { payload }: PayloadAction<string>) => {
      state.error = payload;
    },
    setLoading: (state: ILuxuryHomesState, { payload }: PayloadAction<boolean>) => {
      state.isLoading = payload;
    },
    setDefaultValues: () => {
      return luxuryHomesInitialState;
    },
  },
});

export const luxuryHomesActions = luxuryHomesSlice.actions;

export const luxuryHomesReducer = luxuryHomesSlice.reducer;

export const getLuxuryHomes = (): AppThunk => {
  return async (dispatch) => {
    try {
      dispatch(luxuryHomesActions.setLoading(true));
      dispatch(luxuryHomesSearchActions.setDefaultValues());

      const { data } = await axiosInstance.get(`${Urls.LuxuryHomes}`, {
        ...getHeaders(),
      });
      
      const homesInt = data.map((d: any) => 
                  d.items?.map((i: any) => ({
                      ...i.condoDetails,
                      description: d?.description,
                      multipleUnits: i.condoDetails?.units?.length > 1,
                      location: {
                        id: d.placeId || d?.location?.city,
                        unitsCount: i.condoDetails?.units?.length || 0,
                        name: d?.location?.city,
                        country: d?.location?.country || d?.location?.countryName,
                        image: d?.imagen,
                        description: d?.description,
                        location: {
                          lat: d?.location?.lat,
                          lng: d?.location?.lng,
                        },
                      },
                  }))).flat();

      const homes = homesInt.map((h: any) => h.units?.map((u: any) => ({
        ...u,
        ...h,
        units: [u],
      }))).flat();

      dispatch(luxuryHomesActions.setHomes(homes));

      const locations = data.map((d: any) => ({
        id: d?.placeId || d?.location?.city,
        name: d?.location?.city,
        country: d?.location?.country || d?.location?.countryName,
        image: d?.imagen,
        description: d?.description,
        unitsCount: d?.units?.length || 0,
        location: {
          lat: d?.location?.lat,
          lng: d?.location?.lng,
        },
      })).filter((obj1: any, i: number, arr: any[]) => arr.findIndex((obj2: any) => (obj2.id === obj1.id)) === i);

      locations.sort((a: ILuxuryHomeLocation, b: ILuxuryHomeLocation) => a.name?.toUpperCase().localeCompare(b.name?.toUpperCase()));
      
      dispatch(luxuryHomesActions.setLocations(locations));

      dispatch(luxuryHomesActions.setLoading(false));
    } catch (error: any) {
      dispatch(luxuryHomesActions.setLoading(false));
      dispatch(luxuryHomesActions.setError(error.toString()));
    }
  };
};
